const NEWS_API_BASE = "https://alerta-maceio-api-production.up.railway.app/api";

const NEWS_MACEIO_BOUNDS = { west: -35.80, east: -35.69, north: -9.50, south: -9.71 };

function NewsLocationMap({ lang, lat, lng, bairro }) {
  const mapDivRef = React.useRef(null);
  const known = lat != null && lng != null;

  React.useEffect(() => {
    if (!mapDivRef.current) return;
    const map = L.map(mapDivRef.current, { zoomControl: true, scrollWheelZoom: true, touchZoom: true, attributionControl: false });
    L.tileLayer(window.VIGI_TILE_URL, window.VIGI_TILE_OPTIONS).addTo(map);
    if (known) {
      map.setView([lat, lng], 13);
      L.circle([lat, lng], {
        radius: 900,
        color: "var(--color-brand-primary)",
        weight: 1.5,
        fillColor: "var(--color-brand-primary)",
        fillOpacity: 0.18,
      }).addTo(map).bindPopup(`${t(lang, "approxAreaOf")} ${window.escapeHtml(bairro || t(lang, "approxAreaOfWhere"))}`);
    } else {
      map.fitBounds([[NEWS_MACEIO_BOUNDS.south, NEWS_MACEIO_BOUNDS.west], [NEWS_MACEIO_BOUNDS.north, NEWS_MACEIO_BOUNDS.east]]);
    }
    return () => map.remove();
  }, [lat, lng]);

  return (
    <div style={{ marginTop: 8 }}>
      <div ref={mapDivRef} style={{ height: 140, borderRadius: 9, overflow: "hidden", position: "relative", zIndex: 0, border: "1px solid var(--color-border)" }} />
      {!known && (
        <div style={{ marginTop: 4, fontSize: "var(--text-xs)", color: "var(--color-text-muted)" }}>
          {t(lang, "exactLocationNotFound")}
        </div>
      )}
    </div>
  );
}

const NEWS_LINK_AD_THROTTLE_MS = 2 * 60 * 1000;

function openNewsLink(link) {
  window.open(link, "_blank", "noopener,noreferrer");
}

function NewsItemCard({ lang, n }) {
  const { Icon } = window.VigiALDesignSystem_ff2dec;
  const [open, setOpen] = React.useState(false);
  const [showAd, setShowAd] = React.useState(false);
  const premium = window.isPremium ? window.isPremium() : false;

  function handleReadFullArticle(e) {
    e.preventDefault();
    if (window.trackEvent) window.trackEvent("news_read_article");
    if (premium) { openNewsLink(n.link); return; }
    const last = Number(localStorage.getItem("vigial_ad_last_noticias") || 0);
    const now = Date.now();
    if (now - last < NEWS_LINK_AD_THROTTLE_MS) { openNewsLink(n.link); return; }
    localStorage.setItem("vigial_ad_last_noticias", String(now));
    setShowAd(true);
  }

  return (
    <div style={{ border: "1px solid var(--color-border)", borderRadius: "var(--radius-xl)", padding: 12, marginBottom: 10, fontFamily: "var(--font-body)" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6, flexWrap: "wrap" }}>
        <span style={{ fontSize: "var(--text-xs)", fontWeight: "var(--weight-semibold)", color: "var(--color-brand-deep)", background: "var(--color-brand-tint)", borderRadius: "var(--radius-pill)", padding: "2px 8px" }}>{n.outlet}</span>
        {n.bairro && <span style={{ fontSize: "var(--text-xs)", color: "var(--color-text-muted)" }}>· {n.bairro}</span>}
        {n.publishedAt && <span style={{ fontSize: "var(--text-xs)", color: "var(--color-text-muted)", fontFamily: "var(--font-mono)" }}>· {window.timeAgoLabel(n.publishedAt, lang)}</span>}
      </div>
      <button onClick={() => setOpen((o) => !o)} style={{ display: "block", width: "100%", textAlign: "left", background: "transparent", border: "none", padding: 0, cursor: "pointer", fontFamily: "var(--font-display)", fontWeight: "var(--weight-medium)", fontSize: "var(--text-md)", color: "var(--color-text-primary)", marginBottom: 4 }}>
        {n.title}
      </button>
      {n.summary && <p style={{ fontSize: "var(--text-base)", color: "var(--color-text-secondary)", margin: "0 0 6px" }}>{n.summary}</p>}
      <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <a href={n.link} onClick={handleReadFullArticle} target="_blank" rel="noreferrer" style={{ fontSize: "var(--text-sm)", color: "var(--color-brand-primary)", display: "inline-flex", alignItems: "center", gap: 4 }}>
          <Icon name="external-link" size={12} color="var(--color-brand-primary)" /> {t(lang, "readFullArticleAt")} {n.outlet}
        </a>
        {showAd && <window.InterstitialAd lang={lang} willOpenExternal onClose={() => { setShowAd(false); openNewsLink(n.link); }} />}
        <button onClick={() => setOpen((o) => !o)} style={{ fontSize: "var(--text-sm)", color: "var(--color-text-secondary)", background: "transparent", border: "none", padding: 0, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 4 }}>
          <Icon name="map-pin" size={12} color="var(--color-text-secondary)" /> {open ? t(lang, "hideLocation") : t(lang, "viewLocation")}
        </button>
      </div>
      {open && <NewsLocationMap lang={lang} lat={n.lat} lng={n.lng} bairro={n.bairro} />}
    </div>
  );
}

const NEWS_PAGE_SIZE = 3;

function NoticiasScreen({ lang }) {
  const { Chip } = window.VigiALDesignSystem_ff2dec;
  const premium = window.isPremium ? window.isPremium() : false;
  const visibleFilters = window.FILTERS.filter((f) => !f.premium || premium);
  const [filter, setFilter] = React.useState("90d");
  const [news, setNews] = React.useState(null);
  const [errored, setErrored] = React.useState(false);
  const [visibleCount, setVisibleCount] = React.useState(NEWS_PAGE_SIZE);

  React.useEffect(() => {
    setNews(null);
    setErrored(false);
    setVisibleCount(NEWS_PAGE_SIZE);
    fetch(`${NEWS_API_BASE}/news?window=${filter}`)
      .then((r) => r.json())
      .then(setNews)
      .catch(() => setErrored(true));
  }, [filter]);

  function handleScroll(e) {
    const el = e.target;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 120) {
      setVisibleCount((c) => (news ? Math.min(c + NEWS_PAGE_SIZE, news.length) : c));
    }
  }

  const shown = news ? news.slice(0, visibleCount) : [];

  return (
    <div style={{ padding: 14, flex: 1, overflowY: "auto" }} onScroll={handleScroll}>
      <div style={{ fontSize: "var(--text-xs)", color: "var(--color-text-muted)", marginBottom: 8 }}>
        {t(lang, "newsIntro")}
      </div>
      <div style={{ display: "flex", gap: 6, overflowX: "auto", marginBottom: 10 }}>
        {visibleFilters.map((f) => <Chip key={f.id} active={filter === f.id} onClick={() => setFilter(f.id)}>{t(lang, f.key)}</Chip>)}
      </div>
      {errored && <div style={{ fontSize: "var(--text-sm)", color: "var(--color-text-muted)" }}>{t(lang, "newsError")}</div>}
      {!errored && news === null && <div style={{ fontSize: "var(--text-sm)", color: "var(--color-text-muted)" }}>{t(lang, "newsLoading")}</div>}
      {news && news.length === 0 && <div style={{ fontSize: "var(--text-sm)", color: "var(--color-text-muted)" }}>{t(lang, "newsEmpty")}</div>}
      {shown.map((n, i) => <NewsItemCard key={n.link || i} lang={lang} n={n} />)}
      {news && visibleCount < news.length && (
        <button
          onClick={() => setVisibleCount((c) => Math.min(c + NEWS_PAGE_SIZE, news.length))}
          style={{ display: "block", width: "100%", textAlign: "center", fontSize: "var(--text-sm)", color: "var(--color-brand-primary)", background: "transparent", border: "1px solid var(--color-border)", borderRadius: "var(--radius-lg)", padding: "8px 0", margin: "4px 0 10px", cursor: "pointer", fontFamily: "var(--font-body)" }}
        >
          {t(lang, "loadMoreNews")}
        </button>
      )}
    </div>
  );
}
window.NoticiasScreen = NoticiasScreen;
